/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isnan from '@stdlib/math/base/assert/is-nan';
import sqrt from '@stdlib/math/base/special/sqrt';


// MAIN //

/**
* Returns the median of a triangular distribution.
*
* @param {number} a - minimum support
* @param {number} b - maximum support
* @param {number} c - mode
* @returns {number} median
*
* @example
* var v = median( 0.0, 1.0, 0.5 );
* // returns 0.5
*
* @example
* var v = median( 4.0, 12.0, 9.0 );
* // returns ~8.472
*
* @example
* var v = median( -4.0, 4.0, -1.0 );
* // returns ~-0.472
*
* @example
* var v = median( 1.0, -0.1, 0.5 );
* // returns NaN
*
* @example
* var v = median( 0.0, 1.0, 2.0 );
* // returns NaN
*
* @example
* var v = median( NaN, 4.0, 2.0 );
* // returns NaN
*
* @example
* var v = median( 0.0, NaN, 2.0 );
* // returns NaN
*
* @example
* var v = median( 0.0, 4.0, NaN );
* // returns NaN
*/
function median( a, b, c ) {
	if (
		isnan( a ) ||
		isnan( b ) ||
		isnan( c ) ||
		!( a <= c && c <= b )
	) {
		return NaN;
	}
	if ( c >= ( a+b ) / 2.0 ) {
		return a + sqrt( 0.5 * ( b-a ) * ( c-a ) );
	}
	return b - sqrt( 0.5 * ( b-a ) * ( b-c ) );
}


// EXPORTS //

export default median;
